import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { SectionHeading } from "@/components/Cards";
import { Button } from "@/components/ui/button";
import { CalendarDays, Star as StarIcon, Sparkles, HeartHandshake, ArrowRight, Check, BadgeCheck } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1729335511883-29eade10006b?crop=entropy&cs=srgb&fm=jpg&q=85";

const CARDS = [
  { icon: CalendarDays, slug: "panchang", title: "Vedic Panchang", desc: "Daily Hindu calendar with Tithi, Nakshatra, Yoga, Karana, auspicious & inauspicious timings and festival dates.", to: "/vedic-services/panchang" },
  { icon: StarIcon, slug: "kundli-rashi-dosha", title: "Kundli, Rashi & Dosha", desc: "Your birth chart, moon sign and planetary positions — plus Mangal, Pitra, Kaal Sarp & Shani dosha remedies, all in one reading.", to: "/vedic-services/kundli" },
  { icon: HeartHandshake, slug: "matchmaking", title: "Kundli Matchmaking", desc: "Ashta Koota Guna Milan compatibility analysis for a harmonious marriage.", to: "/vedic-services/matchmaking" },
];

const FEATURES = [
  {
    eyebrow: "Panchang", title: "Know the right time for every act",
    text: "Your daily and monthly Panchang reveals the auspicious windows for worship, travel and new beginnings — and warns of the inauspicious ones.",
    image: "https://images.unsplash.com/photo-1605302977545-3a09913be1dd?crop=entropy&cs=srgb&fm=jpg&q=85",
    to: "/vedic-services/panchang",
    blocks: [
      { h: "Daily rituals", p: "Get the Choghadiya and Abhijit muhurat to time your prayers each day." },
      { h: "Festivals", p: "Never miss a vrat, muhurat or festival with the upcoming calendar." },
    ],
  },
  {
    eyebrow: "Kundli", title: "Read the map of your birth",
    text: "Your birth chart holds the truth of your temperament and destiny. Understand it and you understand your path.",
    image: "https://images.unsplash.com/photo-1729335511904-9b8690184935?crop=entropy&cs=srgb&fm=jpg&q=85",
    to: "/vedic-services/kundli", reverse: true,
    stats: [
      { n: "12", l: "Houses & Rashis mapped to your life areas" },
      { n: "9", l: "Grahas placed to reveal strengths & timing" },
    ],
  },
  {
    eyebrow: "Dosha", title: "Find balance and remove the obstacles",
    text: "A dosha is not a curse. It is an imbalance that can be corrected with the right puja and guidance drawn from ancient wisdom.",
    image: "https://images.unsplash.com/photo-1636227597176-c554bcbee419?crop=entropy&cs=srgb&fm=jpg&q=85",
    to: "/vedic-services/dosha",
    blocks: [
      { h: "Solutions", p: "Traditional pujas and mantras prescribed for each dosha found." },
      { h: "Guidance", p: "Clear, actionable steps so you know exactly what to perform." },
    ],
  },
  {
    eyebrow: "Compatibility", title: "Enter marriage with clear eyes",
    text: "Compatibility is not a game. It is a science of both minds and hearts. Ashta Koota Guna Milan brings clarity before the sacred bond.",
    image: "https://images.unsplash.com/photo-1691886273161-8e27d3292144?crop=entropy&cs=srgb&fm=jpg&q=85",
    to: "/vedic-services/matchmaking", reverse: true,
    checks: ["Compare 36 gunas across eight kootas", "Detect Mangal (Manglik) dosha for both", "Get a clear verdict and remedies where needed"],
  },
];

export default function VedicServices() {
  const [stats, setStats] = useState(null);
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data));
    api.get("/testimonials").then((r) => setTestimonials(r.data));
  }, []);

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0"><img src={HERO} alt="" className="h-full w-full object-cover opacity-90" /><div className="absolute inset-0 bg-gradient-to-r from-background via-background/92 to-background/60" /></div>
        <div className="relative px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-16 lg:py-24">
          <p className="font-devanagari text-accent text-lg mb-2">॥ ज्योतिष ॥</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold tracking-tight max-w-2xl leading-tight">Explore the Vedic sciences</h1>
          <p className="mt-4 text-muted-foreground max-w-xl text-lg">Ancient knowledge to receive the blessings that guide your path — Panchang, Kundli, Dosha remedies and marriage matchmaking.</p>
        </div>
      </section>

      {/* Four service cards */}
      <section className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-14 lg:py-20">
        <SectionHeading eyebrow="Choose a service" title="Four pillars of Vedic guidance" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {CARDS.map((s) => (
            <Link to={s.to} key={s.title} data-testid={`vedic-service-${s.slug}`} className="rounded-2xl bg-card border border-border p-6 hover-lift hover:border-accent group">
              <span className="h-12 w-12 grid place-items-center rounded-xl bg-gradient-to-br from-accent/20 to-primary/20 text-primary mb-4"><s.icon className="h-6 w-6" /></span>
              <h3 className="font-serif text-lg font-semibold">{s.title}</h3>
              <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{s.desc}</p>
              <span className="inline-flex items-center gap-1 text-sm text-primary mt-3 group-hover:gap-2 transition-all">Explore <ArrowRight className="h-4 w-4" /></span>
            </Link>
          ))}
        </div>
      </section>

      {/* Alternating feature sections */}
      <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto pb-4 space-y-16 lg:space-y-24">
        {FEATURES.map((f) => (
          <div key={f.title} data-testid={`vedic-feature-${f.eyebrow.toLowerCase()}`} className="grid lg:grid-cols-2 gap-8 lg:gap-14 items-center">
            <div className={`rounded-3xl overflow-hidden border border-border shadow-sm ${f.reverse ? "lg:order-2" : ""}`}>
              <img src={f.image} alt={f.title} className="w-full h-[260px] lg:h-[380px] object-cover" />
            </div>
            <div className={f.reverse ? "lg:order-1" : ""}>
              <p className="text-xs uppercase tracking-[0.25em] font-semibold text-accent mb-3">{f.eyebrow}</p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif font-semibold tracking-tight leading-tight">{f.title}</h2>
              <p className="mt-4 text-muted-foreground leading-relaxed">{f.text}</p>

              {f.blocks && (
                <div className="grid sm:grid-cols-2 gap-6 mt-7">
                  {f.blocks.map((b) => (
                    <div key={b.h}>
                      <h3 className="font-serif text-base font-semibold flex items-center gap-2"><BadgeCheck className="h-4 w-4 text-accent" /> {b.h}</h3>
                      <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{b.p}</p>
                    </div>
                  ))}
                </div>
              )}
              {f.stats && (
                <div className="grid grid-cols-2 gap-6 mt-7">
                  {f.stats.map((st) => (
                    <div key={st.l}>
                      <p className="font-serif text-4xl font-bold text-primary">{st.n}</p>
                      <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{st.l}</p>
                    </div>
                  ))}
                </div>
              )}
              {f.checks && (
                <ul className="space-y-2.5 mt-7">
                  {f.checks.map((c) => <li key={c} className="flex gap-2 text-sm"><Check className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" /> {c}</li>)}
                </ul>
              )}

              <Link to={f.to} className="inline-block mt-8"><Button variant="outline" data-testid={`feature-explore-${f.eyebrow.toLowerCase()}`} className="rounded-full h-11 px-7 border-accent text-foreground hover:bg-accent hover:text-accent-foreground">Explore <ArrowRight className="ml-1.5 h-4 w-4" /></Button></Link>
            </div>
          </div>
        ))}
      </div>

      {/* Stats */}
      {stats && (
        <section className="bg-secondary/40 border-y border-border mt-16 lg:mt-24">
          <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-14 lg:py-16">
            <SectionHeading center eyebrow="Vedic Wisdom, Quantified" title="Guidance trusted by thousands"
              subtitle="From daily Panchang to life's biggest decisions, devotees turn to Pavitra Mantra for authentic Vedic clarity." />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center mt-4">
              {[["Devotees Guided", (stats.devotees/1000).toFixed(1) + "K+"], ["Verified Purohits", stats.purohits + "+"], ["Worships & Rituals", stats.services], ["Cities", stats.cities]].map(([l, v]) => (
                <div key={l}><p className="font-serif text-4xl font-bold text-primary">{v}</p><p className="text-sm text-muted-foreground mt-1">{l}</p></div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Testimonials */}
      <section className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-14 lg:py-20">
        <SectionHeading center eyebrow="Bhakton ke Vichaar" title="What seekers say" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {testimonials.map((t) => (
            <div key={t.id} data-testid={`vedic-testimonial-${t.id}`} className="rounded-2xl bg-card border border-border p-6">
              <div className="flex gap-0.5 mb-3">{Array.from({ length: t.rating }).map((_, i) => <StarIcon key={i} className="h-4 w-4 fill-accent text-accent" />)}</div>
              <p className="text-sm text-muted-foreground leading-relaxed">"{t.text}"</p>
              <div className="flex items-center gap-3 mt-4 pt-4 border-t border-border">
                <img src={t.image} alt="" className="h-9 w-9 rounded-full object-cover" />
                <div><p className="font-semibold text-sm">{t.name}</p><p className="text-xs text-muted-foreground">{t.city}</p></div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <p className="text-xs text-muted-foreground text-center max-w-2xl mx-auto px-4 pb-10">Note: Panchang and Vedic readings are curated, illustrative samples for demonstration. For personalised readings, book a consultation with our verified purohits.</p>
    </div>
  );
}
